// Banner text for a milestone (or a montage of them), in the player's
// language: a title, a subtitle, and — for the kid layer (ui_mode=icons) —
// a short friendly line plus an icon name.

import { LEGEND_KINDS, type MilestonePayload } from '@aod/protocol';
import type { MessageKey, Translate } from '../i18n/index.js';

export type MilestoneIcon =
  | 'house'
  | 'building'
  | 'castle'
  | 'mapPin'
  | 'flag'
  | 'crown'
  | 'trophy'
  | 'sword'
  | 'sparkles'
  | 'church'
  | 'landmark'
  | 'wand'
  | 'handshake'
  | 'swords'
  | 'hourglass'
  | 'star';

export interface MilestoneCopy {
  readonly title: string;
  readonly subtitle: string;
  readonly kidLine: string;
  readonly icon: MilestoneIcon;
}

const VICTORY_PATHS = ['conquest', 'economy', 'science', 'faith', 'diplomacy'] as const;

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

export function milestoneCopy(
  p: MilestonePayload,
  t: Translate,
  localPlayer: number,
): MilestoneCopy {
  const d = p.data ?? {};
  const city = p.name ?? t('cinematic.cityFallback');
  const hero = p.name ?? t('cinematic.heroFallback');
  const three = (base: string, vars: Record<string, string | number>, icon: MilestoneIcon) => ({
    title: t(`${base}.title` as MessageKey, vars),
    subtitle: t(`${base}.subtitle` as MessageKey, vars),
    kidLine: t(`${base}.kid` as MessageKey, vars),
    icon,
  });

  switch (p.milestoneId) {
    case 'city.tier_up': {
      const tier = num(d.tier);
      const known = tier === 2 || tier === 3 || tier === 4;
      const base = known ? `cinematic.tierUp.${tier}` : 'cinematic.tierUp';
      return three(base, { name: city }, tier === 4 ? 'castle' : tier === 3 ? 'building' : 'house');
    }
    case 'city.first_founded':
      return three('cinematic.firstFounded', { name: city }, 'mapPin');
    case 'city.first_capture':
      return three('cinematic.firstCapture', { name: city }, 'flag');
    case 'city.capital_captured':
      return three('cinematic.capitalCaptured', { name: city }, 'crown');
    case 'hero.first_recruited':
      return three('cinematic.heroRecruited', { name: hero }, 'sword');
    case 'hero.level_milestone':
      return three('cinematic.heroLevel', { name: hero, level: num(d.level) ?? '' }, 'star');
    case 'doctrine.tier_reached':
      return three('cinematic.doctrine', { tier: num(d.tier) ?? '' }, 'sparkles');
    case 'faith.temple_built':
      return three('cinematic.temple', {}, 'church');
    case 'wonder.built':
      return three('cinematic.wonder', { name: p.name ?? '' }, 'landmark');
    case 'magic.ritual_completed':
      return three('cinematic.ritual', {}, 'wand');
    case 'diplomacy.peace_signed': {
      const other = num(d.otherPlayerIndex);
      const player = t('cinematic.playerName', { n: other !== undefined ? other + 1 : '?' });
      return three('cinematic.peace', { player }, 'handshake');
    }
    case 'battle.decisive':
      return three('cinematic.battle', {}, 'swords');
    case 'era.advanced':
      return three('cinematic.era', { era: num(d.era) ?? '' }, 'hourglass');
    case 'legend.summoned':
    case 'legend.fallen': {
      // The milestone data names the unit kind (dragon / golem / lich): the subtitle and the
      // kid line name the creature; an unknown kind keeps the generic copy.
      const summoned = p.milestoneId === 'legend.summoned';
      const base = summoned ? 'cinematic.legendSummoned' : 'cinematic.legendFallen';
      const kind = LEGEND_KINDS.find((k) => k === d.legend);
      const generic = three(base, {}, summoned ? 'sparkles' : 'flag');
      if (!kind) return generic;
      return {
        ...generic,
        subtitle: summoned ? t(`${base}.subtitle.${kind}` as MessageKey) : generic.subtitle,
        kidLine: t(`${base}.kid.${kind}` as MessageKey),
      };
    }
    case 'sea.merchant_route':
      return three('cinematic.merchantRoute', {}, 'handshake');
    case 'graduation.new_rule':
      return three(
        'cinematic.graduation',
        // The client (rulesets R8) always supplies the module label.
        { name: String(d.name ?? '') },
        'sparkles',
      );
    case 'victory.achieved': {
      if (p.playerIndex !== localPlayer) {
        const player = t('cinematic.playerName', { n: p.playerIndex + 1 });
        return three('cinematic.defeat', { player }, 'flag');
      }
      const path = VICTORY_PATHS.find((v) => v === d.path);
      return {
        title: t('cinematic.victory.title'),
        subtitle: t(path ? `cinematic.victory.${path}` : 'cinematic.victory.subtitle'),
        kidLine: t('cinematic.victory.kid'),
        icon: 'trophy',
      };
    }
  }
}

/** Montage: one banner for several milestones — their titles, the first few by name. */
export function montageCopy(
  milestones: ReadonlyArray<MilestonePayload>,
  t: Translate,
  localPlayer: number,
  shown = 3,
): MilestoneCopy {
  const titles = milestones.map((m) => milestoneCopy(m, t, localPlayer).title);
  const rest = titles.length - shown;
  const subtitle = [
    ...titles.slice(0, shown),
    ...(rest > 0 ? [t('cinematic.montage.more', { count: rest })] : []),
  ].join(' · ');
  return {
    title: t('cinematic.montage.title'),
    subtitle,
    kidLine: t('cinematic.montage.kid'),
    icon: 'star',
  };
}

export function copyFor(
  milestones: ReadonlyArray<MilestonePayload>,
  t: Translate,
  localPlayer: number,
): MilestoneCopy {
  if (milestones.length === 1)
    return milestoneCopy(milestones[0] as MilestonePayload, t, localPlayer);
  return montageCopy(milestones, t, localPlayer);
}
